// apps/roleforge-app/src/components/audit/VoicesTab.tsx
//
// Phase 11 task 11.2 — owner-side stakeholder thread browser. Lists every
// voice contributing to the audit (owner first, then stakeholders) with
// per-voice contribution counts. Stakeholder rows expose a Mark-as-complete
// button that force-closes the thread (owner-only force-close path).

import { useEffect, useState } from 'react';
import {
  listThreads,
  forceCloseThread,
  type ThreadSummary,
} from '../../lib/api-client';

interface Props {
  auditId: string;
  jwt?: string | null;
  onSelectVoice: (threadId: string) => void;
}

export function VoicesTab({ auditId, jwt, onSelectVoice }: Props) {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!jwt) {
      setError('Missing session');
      return () => {
        cancelled = true;
      };
    }
    void listThreads(jwt, auditId)
      .then((res) => {
        if (!cancelled) setThreads(res.threads);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, auditId]);

  const handleClose = async (threadId: string, voiceLabel: string) => {
    if (!jwt) {
      setError('Missing session');
      return;
    }
    if (
      !window.confirm(
        `This freezes ${voiceLabel}'s contributions to the audit. They can still see what they wrote, but can't add more.`,
      )
    )
      return;
    await forceCloseThread(jwt, threadId);
    setThreads((prev) =>
      prev.map((t) =>
        t.id === threadId ? { ...t, status: 'completed_owner' } : t,
      ),
    );
  };

  if (error) {
    return (
      <div className="p-4 text-sm text-coral">
        Couldn't load voices: {error}
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="p-4 text-sm text-graphite">
        No voices yet — invite a teammate to start.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {threads.map((t) => (
        <div
          key={t.id}
          className="flex flex-col gap-2 rounded-xl border border-line bg-card p-3"
        >
          <div className="flex items-center justify-between gap-2">
            <button
              data-testid="voice-row-label"
              onClick={() => onSelectVoice(t.id)}
              className="truncate text-sm font-semibold text-ink hover:underline"
            >
              {t.voice_label}
            </button>
            <StatusPill status={t.status} />
          </div>
          <div className="text-xs text-graphite">
            {t.contribution_counts.pain_points} pain point
            {t.contribution_counts.pain_points === 1 ? '' : 's'} ·{' '}
            {t.contribution_counts.opportunities} opportunit
            {t.contribution_counts.opportunities === 1 ? 'y' : 'ies'}
          </div>
          {t.kind === 'stakeholder' && t.status !== 'completed_owner' && (
            <button
              onClick={() => handleClose(t.id, t.voice_label)}
              className="self-start text-[11px] text-graphite hover:text-coral"
            >
              Mark as complete
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: ThreadSummary['status'] }) {
  const map = {
    active: { label: 'In progress', cls: 'bg-mint/15 text-mint' },
    completion_proposed: {
      label: 'Wrapping up',
      cls: 'bg-coral/15 text-coral',
    },
    completed_self: { label: 'Completed', cls: 'bg-slate-700 text-slate-300' },
    completed_owner: { label: 'Completed', cls: 'bg-slate-700 text-slate-300' },
    revoked: { label: 'Withdrawn', cls: 'bg-slate-800 text-slate-500' },
  } as const;
  const { label, cls } = map[status];
  return (
    <span
      className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}
